'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      router.replace('/');
    } catch (err: any) {
      setError(err?.message || 'Authentication failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      <div className="absolute inset-0 scan-line pointer-events-none" />

      {/* Glow orb */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <AppLogo size={48} />
            <div>
              <h1 className="text-2xl font-bold text-primary tracking-widest text-glow-green">LEAFLINK</h1>
              <p className="text-[10px] text-muted-foreground tracking-widest uppercase">Space LEAF Corp · NOC Access</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">Authorized personnel only. All access is monitored.</p>
        </div>

        {/* Login card */}
        <div className="card-elevated p-8 border border-primary/20">
          <div className="flex items-center gap-2 mb-6">
            <Icon name="ShieldCheckIcon" size={18} className="text-primary" />
            <h2 className="text-base font-semibold text-foreground">Secure Authentication</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
                Engineer ID / Email
              </label>
              <div className="relative">
                <Icon name="UserIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="engineer@leaflink.space"
                  className="w-full pl-9 pr-4 py-2.5 bg-muted border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/20 transition-colors font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
                Access Code
              </label>
              <div className="relative">
                <Icon name="LockClosedIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••••••"
                  className="w-full pl-9 pr-10 py-2.5 bg-muted border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/20 transition-colors font-mono"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={14} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-red-500/10 border border-red-500/20">
                <Icon name="ExclamationTriangleIcon" size={14} className="text-red-400 flex-shrink-0" />
                <p className="text-xs text-red-400 font-mono">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-md bg-primary text-background font-semibold text-sm tracking-wider uppercase transition-all hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                  <span>Authenticating…</span>
                </>
              ) : (
                <>
                  <Icon name="ShieldCheckIcon" size={16} />
                  <span>Access NOC Dashboard</span>
                </>
              )}
            </button>
          </form>

          {/* Security notice */}
          <div className="mt-6 pt-5 border-t border-border">
            <div className="flex items-start gap-2">
              <Icon name="InformationCircleIcon" size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                This system is restricted to authorized Space LEAF Corp personnel. Unauthorized access attempts are logged and reported to security operations.
              </p>
            </div>
          </div>
        </div>

        {/* Diamond Firewall status */}
        <div className="mt-4 flex items-center justify-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-primary node-pulse" />
          <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">Diamond Firewall · Active</span>
        </div>
      </div>
    </div>
  );
}
